/**
 * Approval Matrix Master Page
 * Configure dynamic approval routing per Business Division / Department / Employee.
 * Beneficiary-based approval workflows & ticket booking routing.
 */
import React, { useEffect, useState } from 'react';
import MainLayout from '../components/MainLayout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Plus, Trash2, Edit2, CheckCircle2, XCircle, Search,
  Network, Sparkles, UserCheck, ShieldCheck, Ticket, Calendar,
  ChevronRight, RefreshCw, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ApprovalMatrixMaster() {
  const [matrices, setMatrices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    business_division: '',
    department: '',
    level_1_approver: 'Requester',
    level_2_approver: '',
    level_3_approver: '',
    ticket_booking_person: 'Travel Desk',
    effective_from: '2020-01-01',
    effective_to: '2099-12-31',
    is_active: true,
    priority: 10
  });

  // Preview / Test Tool State
  const [testEmpId, setTestEmpId] = useState('');
  const [resolvedPreview, setResolvedPreview] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    loadMatrices();
    loadEmployees();
  }, []);

  const loadMatrices = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/approval-matrix`, { withCredentials: true });
      setMatrices(Array.isArray(res.data?.matrices) ? res.data.matrices : []);
    } catch (e) {
      toast.error('Failed to load approval matrices: ' + (e?.response?.data?.detail || e.message));
    }
    setLoading(false);
  };

  const loadEmployees = async () => {
    try {
      const res = await axios.get(`${API}/employees?limit=500`, { withCredentials: true });
      const empList = Array.isArray(res.data) ? res.data : (res.data?.employees || []);
      setEmployees(empList.map(emp => {
        const pd = emp.personal_details || {};
        const ed = emp.employment_details || {};
        return {
          id: emp.id || emp._id,
          code: ed.employee_code || emp.employee_code || '',
          name: `${pd.first_name || ''} ${pd.last_name || ''}`.strip() || emp.name || 'Employee',
          dept: ed.department_name || ed.department || emp.department || '',
          div: ed.business_division || ed.division || emp.business_division || ''
        };
      }));
    } catch (e) {
      console.error('Failed to load employee list', e);
    }
  };

  const seedDefaults = async () => {
    try {
      const res = await axios.post(`${API}/approval-matrix/seed-defaults`, {}, { withCredentials: true });
      toast.success(res.data?.message || 'Default approval matrices seeded!');
      loadMatrices();
    } catch (e) {
      toast.error('Failed to seed defaults: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const handleOpenModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        business_division: item.business_division || '',
        department: item.department || '',
        level_1_approver: item.level_1_approver || 'Requester',
        level_2_approver: item.level_2_approver || '',
        level_3_approver: item.level_3_approver || '',
        ticket_booking_person: item.ticket_booking_person || 'Travel Desk',
        effective_from: item.effective_from || '2020-01-01',
        effective_to: item.effective_to || '2099-12-31',
        is_active: item.is_active !== false,
        priority: item.priority || 10
      });
    } else {
      setEditingItem(null);
      setFormData({
        business_division: '',
        department: '',
        level_1_approver: 'Requester',
        level_2_approver: '',
        level_3_approver: '',
        ticket_booking_person: 'Travel Desk',
        effective_from: '2020-01-01',
        effective_to: '2099-12-31',
        is_active: true,
        priority: 10
      });
    }
    setIsModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.business_division.trim() || !formData.department.trim()) {
      toast.error('Business Division and Department are required.');
      return;
    }

    setSaving(true);
    try {
      if (editingItem) {
        await axios.put(`${API}/approval-matrix/${editingItem.id || editingItem._id}`, formData, { withCredentials: true });
        toast.success('Approval Matrix rule updated successfully!');
      } else {
        await axios.post(`${API}/approval-matrix`, formData, { withCredentials: true });
        toast.success('Approval Matrix rule created successfully!');
      }
      setIsModalOpen(false);
      loadMatrices();
    } catch (e) {
      toast.error('Save failed: ' + (e?.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this approval matrix rule?')) return;
    try {
      await axios.delete(`${API}/approval-matrix/${id}`, { withCredentials: true });
      toast.success('Approval matrix rule deleted.');
      loadMatrices();
    } catch (e) {
      toast.error('Delete failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const testResolution = async () => {
    if (!testEmpId) {
      toast.error('Select an employee to preview matrix resolution.');
      return;
    }
    setResolving(true);
    try {
      const res = await axios.post(`${API}/approval-matrix/resolve`, {
        beneficiary_employee_id: testEmpId
      }, { withCredentials: true });
      setResolvedPreview(res.data?.resolved_matrix || null);
    } catch (e) {
      toast.error('Resolution preview failed: ' + (e?.response?.data?.detail || e.message));
      setResolvedPreview(null);
    }
    setResolving(false);
  };

  const filteredMatrices = matrices.filter(m => {
    const matchesSearch = (m.business_division || '').toLowerCase().includes(search.toLowerCase()) ||
                          (m.department || '').toLowerCase().includes(search.toLowerCase()) ||
                          (m.level_1_approver || '').toLowerCase().includes(search.toLowerCase()) ||
                          (m.level_2_approver || '').toLowerCase().includes(search.toLowerCase()) ||
                          (m.level_3_approver || '').toLowerCase().includes(search.toLowerCase());

    const matchesStatus = activeFilter === 'all' ||
                          (activeFilter === 'active' && m.is_active) ||
                          (activeFilter === 'inactive' && !m.is_active);

    return matchesSearch && matchesStatus;
  });

  return (
    <MainLayout>
      <div className="p-6 space-y-6" data-testid="approval-matrix-master-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Network className="w-7 h-7 text-indigo-600" />
              Approval Matrix Master
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Configure dynamic approval chains (L1, L2, L3) & ticket booking personnel derived strictly by the <strong>Beneficiary Employee</strong>'s division & department.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={seedDefaults} variant="outline" className="border-indigo-200 text-indigo-700 hover:bg-indigo-50">
              <Sparkles className="w-4 h-4 mr-1.5 text-indigo-600" />
              Seed Default Configurations
            </Button>
            <Button onClick={() => handleOpenModal()} className="bg-indigo-600 hover:bg-indigo-700 text-white">
              <Plus className="w-4 h-4 mr-1.5" />
              Add Matrix Rule
            </Button>
          </div>
        </div>

        {/* Live Resolution Preview Tester */}
        <Card className="p-4 bg-gradient-to-r from-indigo-50/70 via-purple-50/50 to-white border border-indigo-150 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <UserCheck className="w-5 h-5 text-indigo-600" />
            <h3 className="text-sm font-semibold text-slate-900">Preview Hierarchy Resolution by Beneficiary</h3>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[240px]">
              <select
                value={testEmpId}
                onChange={e => setTestEmpId(e.target.value)}
                className="w-full text-sm bg-white border border-slate-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Select a Beneficiary Employee to Test…</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.name} ({emp.code}) — {emp.div || 'No Division'} / {emp.dept || 'No Dept'}
                  </option>
                ))}
              </select>
            </div>
            <Button onClick={testResolution} disabled={resolving} className="bg-indigo-600 hover:bg-indigo-700 text-white">
              {resolving ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <ChevronRight className="w-4 h-4 mr-1" />}
              Resolve Matrix
            </Button>
          </div>

          {resolvedPreview && (
            <div className="mt-4 p-4 bg-white rounded-lg border border-indigo-100 shadow-inner space-y-3">
              <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                <div>
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Beneficiary</span>
                  <p className="text-sm font-bold text-indigo-900">{resolvedPreview.beneficiary_employee_name} ({resolvedPreview.beneficiary_employee_code})</p>
                </div>
                <div className="text-right">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Division / Department</span>
                  <p className="text-sm font-semibold text-slate-800">{resolvedPreview.beneficiary_division || 'General'} → {resolvedPreview.beneficiary_department || 'General'}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 pt-2">
                {resolvedPreview.approvals?.map((appr, idx) => (
                  <div key={idx} className="p-3 bg-indigo-50/60 rounded-md border border-indigo-100">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-indigo-700">Level {idx + 1} Approver</span>
                    <p className="text-sm font-bold text-slate-900 mt-0.5">{appr.approver_name}</p>
                    <span className="text-[10px] text-slate-500 capitalize">Type: {appr.approver_type} ({appr.approver_role})</span>
                  </div>
                ))}
                <div className="p-3 bg-emerald-50/60 rounded-md border border-emerald-100">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 flex items-center gap-1">
                    <Ticket className="w-3 h-3" /> Ticket Booking Person
                  </span>
                  <p className="text-sm font-bold text-slate-900 mt-0.5">{resolvedPreview.ticket_booking_person || 'Travel Desk'}</p>
                  <span className="text-[10px] text-slate-500">Auto-assigned for booking</span>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Filter & Search Toolbar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-white p-3 rounded-lg border border-slate-200">
          <div className="relative flex-1 w-full max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
            <input
              type="text"
              placeholder="Search by Business Division, Department, or Approver name…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-md outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-600">Status:</span>
            {['all', 'active', 'inactive'].map(st => (
              <button
                key={st}
                onClick={() => setActiveFilter(st)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize border transition-all ${
                  activeFilter === st
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                }`}
              >
                {st}
              </button>
            ))}
          </div>
        </div>

        {/* Matrix Table */}
        <Card className="overflow-hidden border border-slate-200">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-[11px] font-bold uppercase text-slate-600 tracking-wider">
                  <th className="py-3 px-4">Priority</th>
                  <th className="py-3 px-4">Business Division</th>
                  <th className="py-3 px-4">Department / Function</th>
                  <th className="py-3 px-4">Level 1 Approver</th>
                  <th className="py-3 px-4">Level 2 Approver</th>
                  <th className="py-3 px-4">Level 3 Approver</th>
                  <th className="py-3 px-4">Ticket Booking Person</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 text-xs text-slate-800">
                {loading ? (
                  <tr>
                    <td colSpan={9} className="py-8 text-center text-slate-500">
                      Loading approval matrix rules…
                    </td>
                  </tr>
                ) : filteredMatrices.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="py-12 text-center text-slate-500">
                      <AlertCircle className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                      No approval matrix rules found matching filter.
                    </td>
                  </tr>
                ) : (
                  filteredMatrices.map((m) => (
                    <tr key={m.id || m._id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-3 px-4 font-mono font-semibold text-slate-600">#{m.priority || 10}</td>
                      <td className="py-3 px-4 font-semibold text-indigo-900">{m.business_division}</td>
                      <td className="py-3 px-4 font-semibold text-slate-900">{m.department}</td>
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-blue-50 text-blue-800 font-medium border border-blue-200">
                          {m.level_1_approver || 'Requester'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {m.level_2_approver ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded bg-purple-50 text-purple-800 font-medium border border-purple-200">
                            {m.level_2_approver}
                          </span>
                        ) : (
                          <span className="text-slate-400 italic">(Skipped)</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {m.level_3_approver ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded bg-amber-50 text-amber-800 font-medium border border-amber-200">
                            {m.level_3_approver}
                          </span>
                        ) : (
                          <span className="text-slate-400 italic">(Skipped)</span>
                        )}
                      </td>
                      <td className="py-3 px-4 font-medium text-emerald-700">
                        {m.ticket_booking_person || 'Travel Desk'}
                      </td>
                      <td className="py-3 px-4">
                        {m.is_active ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                            <CheckCircle2 className="w-3 h-3" /> Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600">
                            <XCircle className="w-3 h-3" /> Inactive
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right space-x-1">
                        <Button size="sm" variant="ghost" onClick={() => handleOpenModal(m)} className="h-7 w-7 p-0 text-indigo-600 hover:bg-indigo-50">
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(m.id || m._id)} className="h-7 w-7 p-0 text-rose-600 hover:bg-rose-50">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Modal Dialog for Add / Edit */}
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 space-y-4 border border-slate-200">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-indigo-600" />
                  {editingItem ? 'Edit Approval Matrix Rule' : 'Create Approval Matrix Rule'}
                </h3>
                <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600">✕</button>
              </div>

              <form onSubmit={handleSave} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-700">Business Division *</label>
                    <input
                      type="text"
                      required
                      value={formData.business_division}
                      onChange={e => setFormData({ ...formData, business_division: e.target.value })}
                      placeholder="e.g. Consultancy / Lab / Project"
                      className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700">Department / Function *</label>
                    <input
                      type="text"
                      required
                      value={formData.department}
                      onChange={e => setFormData({ ...formData, department: e.target.value })}
                      placeholder="e.g. CRZ / EC / Chennai / Infra"
                      className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 outline-none"
                    />
                  </div>
                </div>

                <div className="space-y-2 border-t border-slate-100 pt-3">
                  <span className="text-xs font-bold uppercase tracking-wider text-indigo-900">Approval Hierarchy Setup</span>
                  <div>
                    <label className="text-xs font-semibold text-slate-700">Level 1 Approver *</label>
                    <input
                      type="text"
                      required
                      value={formData.level_1_approver}
                      onChange={e => setFormData({ ...formData, level_1_approver: e.target.value })}
                      placeholder="e.g. Requester / Reporting Manager"
                      className="w-full mt-1 px-3 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700">Level 2 Approver (optional)</label>
                    <input
                      type="text"
                      value={formData.level_2_approver}
                      onChange={e => setFormData({ ...formData, level_2_approver: e.target.value })}
                      placeholder="e.g. Rao / Tamilselvan / Diwagar / Ganesh (Leave blank to skip)"
                      className="w-full mt-1 px-3 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700">Level 3 Approver (optional)</label>
                    <input
                      type="text"
                      value={formData.level_3_approver}
                      onChange={e => setFormData({ ...formData, level_3_approver: e.target.value })}
                      placeholder="e.g. CEO/CEO Office / Sharon / DT (Leave blank to skip)"
                      className="w-full mt-1 px-3 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 outline-none"
                    />
                  </div>
                </div>

                <div className="border-t border-slate-100 pt-3 space-y-2">
                  <div>
                    <label className="text-xs font-semibold text-slate-700">Ticket Booking Person</label>
                    <input
                      type="text"
                      value={formData.ticket_booking_person}
                      onChange={e => setFormData({ ...formData, ticket_booking_person: e.target.value })}
                      placeholder="e.g. Travel Desk / Specific User Name"
                      className="w-full mt-1 px-3 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 outline-none"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-slate-700">Priority Order</label>
                      <input
                        type="number"
                        value={formData.priority}
                        onChange={e => setFormData({ ...formData, priority: parseInt(e.target.value) || 1 })}
                        className="w-full mt-1 px-3 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 outline-none"
                      />
                    </div>
                    <div className="flex items-center pt-5">
                      <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-slate-700">
                        <input
                          type="checkbox"
                          checked={formData.is_active}
                          onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
                          className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                        />
                        Active Status
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-100">
                  <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                    {saving ? 'Saving…' : (editingItem ? 'Update Rule' : 'Create Rule')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
